
import { Component, Vue, Prop, Watch, Inject } from "vue-property-decorator";
import { namespace, Getter, Mutation, Action } from "vuex-class";
import externalComponent from "@/utils/external-component";
import { ViewMode, ViewCellObject, ViewObject, CellPosition } from "@/store/views/types.ts";
import { get } from "lodash";
import vClickOutside from "v-click-outside";
import ErrorPlugin from "@/components/views/ErrorPlugin.vue";
import ViewsEventBus from "@/services/views/viewsEventBus";

const ViewState = namespace("views");

@Component({
	components: {
		"error-plugin": ErrorPlugin
	},
	directives: {
		clickOutside: vClickOutside.directive
	}
})
export default class ViewCell extends Vue {
	@ViewState.Action saveCellContent: ({ viewId, viewCellId, content }) => Promise<void>;

	@ViewState.Mutation setContents;
	@ViewState.Mutation deleteCell: ({ viewCellId }) => void;

	@ViewState.Getter("getPluginTypes") pluginTypes;
	@ViewState.Getter getViewMode: ViewMode;
	@ViewState.Getter("getSelectedView") selectedView: ViewObject;

	@Prop({ type: Object, default: {} }) public viewCell: ViewCellObject;
	@Prop({ type: Boolean, default: true }) public canDelete: boolean;
	@Prop({ type: Object, default: () => { return {} } }) public cellPosition: CellPosition;

	@Inject("viewsEventBus") public eventBus: ViewsEventBus;

	private loadedPlugin: any = {};
	private hasFocus: boolean = false;
	private hasError: boolean = false;

	/**
	 * Returns true if Views is currently in Add mode (adding plugins).
	 */
	public get adding() {
		return this.getViewMode === ViewMode.Add;
	}

	/**
	 * Returns true if Views is currently in Edit mode (re-sizing/moving plugins).
	 */
	public get editing() {
		return this.getViewMode === ViewMode.Edit;
	}

	/**
	 * Gets the name of the plugin within this view cell.
	 */
	public get componentName(): string {
		let pluginId = get(this.viewCell, ["pluginTypeId"]);

		this.loadedPlugin = this.pluginTypes.find(p => p.id == pluginId);
		let title = this.loadedPlugin ? this.loadedPlugin.title : null;
		return title;
	}

	/**
	 * Returns true if the user has access to this plugin, false otherwise.
	 */
	public get userHasPluginPermission() {
		return this.loadedPlugin ? this.loadedPlugin.userHasPermissions || false : false;
	}

	/**
	 * Resolves our external plugin component by loading its JS and CSS
	 * into the current page within a <script> tag.
	 */
	public get computedComponent() {
		let componentName = this.componentName;

		if (!componentName) {
			return null;
		}

		if (!this.loadedPlugin.userHasPermissions) {
			return ErrorPlugin;
		}

		// Transform the component name (all lowercase, replace spaces with dashes)
		componentName = componentName
			.toLowerCase()
			.split(" ")
			.join("-");

		return async () => {
			try {
				return await externalComponent(componentName);
			} catch (ex) {
				console.error(`Could not load plugin: ${componentName}`);
				this.hasError = true;
				return ErrorPlugin;
			}
		};
	}

	public mounted() {
		this.eventBus.$on("focus-set", this.onFocusSet);
	}

	private beforeDestroy(): void {
		this.eventBus.$off("focus-set", this.onFocusSet);
	}

	/**
	 * Handler for the 'focus-set' event, triggered by the user pressing a keyboard shortcut
	 * to highlight a certain cell.
	 */
	public onFocusSet(eventData: { viewCellId: number }) {
		if (this.viewCell.viewCellId === eventData.viewCellId) {
			this.onClickInside();
		} else {
			this.onClickOutside(null);
		}
	}

	/**
	 * Returns the human-readable title of the plugin in this cell.
	 */
	public pluginTitle(pluginId: number) {
		const cellPlugin = this.pluginTypes.find(plugin => plugin.id === pluginId);

		if (cellPlugin) {
			return cellPlugin.title;
		}

		return "";
	}

	/**
	 * Handler for when a user updates the content of a plugin. The plugin
	 * will raise this event to us, along with its new content.
	 */
	public async contentChanged(newContent: any) {
		// Ensure there is a view selected
		if (!this.selectedView)
			return;

		// Ensure this cell is a valid cell (temporary ones have negative IDs).
		if (this.viewCell.viewCellId <= 0) {
			return;
		}

		// Update the content in the DB
		await this.saveCellContent({
			viewId: this.selectedView.viewId,
			viewCellId: this.viewCell.viewCellId,
			content: newContent
		});

		// Update in the store
		this.setContents({
			viewCellId: this.viewCell.viewCellId,
			contents: newContent
		});
	}

	/**
	 * Handler for when the user has clicked the 'delete' cross in the upper right
	 * corner. Removes this cell from the current view and triggers a View update.
	 */
	public deleteCellClicked() {
		this.deleteCell({
			viewCellId: this.viewCell.viewCellId
		});
	}

	/**
	 * Handler for when a user clicks on an element outside the boundaries of this
	 * View Cell.
	 */
	public onClickOutside(event) {
		let eventTarget = event?.target;

		if (eventTarget && this.$el.contains(eventTarget)) {
			return;
		}

		this.hasFocus = false;
	}

	/**
	 * Handler for when the user clicks within this View Cell, sets our 'hasFocus'
	 * property to true. This is passed to the plugin to allow keyboard shortcuts to be turned
	 * on/off depending on which plugin the user currently has focused.
	 */
	public onClickInside() {
		if (this.hasError) return;
		this.hasFocus = true;
	}
}
